import { isPaddleSubscriberInFreeTrial, SubscriptionStatus, UserLicenseType } from './data-model/couch-user';

/**
 * The provider capability matrix for admin subscription actions (#4248 Phase 2).
 *
 * It replaces the single PayPro-only guard that used to live in
 * `adminChangeSubscription.ts` (the `if (!userInfo.payProData?.subscriptionId …)`
 * check). Instead of turning every non-PayPro request into the misleading
 * "doesn't have a subscription", this decides — per (action, licenseType, user) —
 * whether the action is supported, or is refused with a plain-language reason
 * that tells support exactly why and what the customer must do instead.
 *
 * It is a PURE function so both the server (which enforces it) and the Admin UI
 * (which gates the buttons on it) read the same rules, and every later phase
 * declares a new action's provider support in exactly one place.
 */

/** The minimal view of a user record the capability rules read. Only the
 *  provider-side subscription identifiers matter — no secrets. */
export interface ActionCapabilityUserView {
  /** The reconciled top-level status. The Resume rules read this as well as the
   *  per-provider block, because the block copy is filled in lazily and the two
   *  can legitimately disagree on a not-yet-reconciled document. */
  subscriptionStatus?: SubscriptionStatus;
  // the per-block `subscriptionStatus` and `scheduledChange` are read by the Resume rules,
  // which have to know whether there is anything to undo. Still no secrets —
  // these are plain state fields.
  payProData?: { subscriptionId?: string; subscriptionStatus?: SubscriptionStatus };
  paddleData?: {
    subscriptionId?: string;
    subscriptionStatus?: SubscriptionStatus;
    scheduledChange?: { action: string };
    // read by the SwitchPlan rule, which must refuse during a free trial. Plain
    // state fields like the two above — no secrets.
    inTrialPeriod?: boolean;
    paidStartCounted?: boolean;
    /** The discount Paddle has attached, read by the SwitchPlan rule (which
     *  refuses while a legacy-price grant would be lost) and by
     *  GrantLegacyPrice (which refuses to grant twice). A plain state field
     *  like the others — no secrets. (#5084) */
    discountId?: string;
  };
  appleData?: { originalTransactionId?: string };
  googlePlayData?: { purchaseToken?: string; productId?: string };
  /** The Paddle per-seat COACHING subscription (#4755). This is a different
   *  subscription from `paddleData`: a coach can hold both, so the two must
   *  never be confused. `isTestPurchase` says the purchase was made in the
   *  Paddle sandbox and is read by `CancelTestCoachSeat`; `subscriptionStatus`
   *  and `scheduledChange` are read by the coach-seat Cancel and Resume rules
   *  (#4418) to answer "is there anything to undo". Plain state fields like the
   *  rest, no secrets. */
  coachSeatData?: {
    subscriptionId?: string;
    isTestPurchase?: boolean;
    subscriptionStatus?: SubscriptionStatus;
    scheduledChange?: { action: string };
  };
  /** The coach's APP-STORE coaching subscription (#4362) — the sibling rail to
   *  `coachSeatData`, bought by the coach inside Apple or Google Play. Only
   *  `provider` is read, and only so a refusal can name the right store and its
   *  own Subscriptions route rather than guessing. A plain state field, no
   *  secrets, like the rest of this view. */
  coachTierData?: {
    provider?: 'apple' | 'googlePlay';
    /** The provider-side handles the coach-tier reconciler reads from (#4416),
     *  one store each: Apple validates by `originalTransactionId`, Google Play
     *  by `purchaseToken` AND `productId`. Presence-tested only and never
     *  printed — the same convention `googlePlayData.purchaseToken` above
     *  already follows on the personal rail. (A purchase token is a credential
     *  where it is DISPLAYED, which is `subscription-ops-summary.ts`'s problem;
     *  this view is a different thing and already holds one.) */
    originalTransactionId?: string;
    purchaseToken?: string;
    productId?: string;
  };
}

/**
 * The result of a capability check. A single shape (rather than a discriminated
 * union) on purpose: the server's tsconfig runs with `strictNullChecks` off,
 * where a boolean-discriminant union does not narrow reliably (`if
 * (!cap.supported)` would not expose `cap.reason`). `reason` is always present
 * when `supported` is false and always omitted when true — every constructor
 * below honors that.
 */
export interface ActionCapability {
  supported: boolean;
  /** Human-readable refusal reason. Present iff `supported` is false. */
  reason?: string;
}

/**
 * The message shown when an admin tries to cancel an Apple subscription. We
 * cannot cancel an App Store subscription on the customer's behalf — only the
 * customer can, from their own device — so we say so and name the exact route.
 */
export const APPLE_CANCEL_REFUSAL =
  'We cannot cancel an Apple subscription on the customer\'s behalf. '
  + 'The customer must cancel it on their iPhone: Settings → Apple Account → Subscriptions '
  + '(App Store subscriptions are managed by Apple, not by us).';

/** The message shown when an admin tries to cancel a Google Play subscription. */
export const GOOGLE_PLAY_CANCEL_REFUSAL =
  'We cannot cancel a Google Play subscription on the customer\'s behalf. '
  + 'The customer must cancel it in the Google Play Store: Menu → Subscriptions '
  + '(Google Play subscriptions are managed by Google, not by us).';

/** Refusal for a PayPro-only action when the user has no live PayPro sub. */
function refusePayProOnlyAction(action: string, licenseType: UserLicenseType | undefined): ActionCapability {
  const onAnotherProvider = licenseType !== undefined && licenseType !== UserLicenseType.PayPro;
  const providerNote = onAnotherProvider ? ` (this user's subscription is ${licenseType})` : '';
  return {
    supported: false,
    reason: `'${action}' is a PayPro-only action and this user has no active PayPro subscription${providerNote}.`,
  };
}

/** Supported when the web-rail subscription id is present, otherwise refused with
 *  a provider-named reason. Shared by the Paddle and PayPro cancel branches so the
 *  two do not carry a near-identical copy of the same check. */
function requireWebRailSubId(subscriptionId: string | undefined, providerLabel: string): ActionCapability {
  return subscriptionId
    ? { supported: true }
    : { supported: false, reason: `This user has no ${providerLabel} subscription to cancel.` };
}

/*
 * Coach subscriptions (#4362, #4416, #4418).
 *
 * The two coach rails get deliberately different answers, and that is the one
 * thing to hold in mind while reading the `case` arms below:
 *
 *  - `CoachSeat` — the Paddle per-seat WEB rail. Cancel, Resume (#4418) and
 *    Re-sync (#4416) are SUPPORTED, under the same state rules as the personal
 *    Paddle rail with one deliberate difference: a PAUSED seat is refused
 *    Cancel, because the only cancel Paddle allows then is immediate and
 *    irreversible. Every other refusal this file gives a CoachSeat coach is
 *    PERMANENT.
 *  - `CoachTier` — the Apple / Google Play APP-STORE rail. Re-sync is supported
 *    (#4416). Every other action is refused PERMANENTLY and routes the COACH
 *    through their own store, because no store gives a merchant an API to act
 *    on somebody else's purchase.
 *
 * Why a coach reaches these branches at all, what each action tells each rail,
 * and the behaviour changes this made:
 * `design-docs/subscription-ops-tooling.md`, "Coach subscriptions".
 */

/** The opening every coach-seat refusal shares: what this subscription IS. Said
 *  first because the old refusals denied it existed. */
const COACH_SEAT_PREFIX =
  'This is a coaching subscription, billed per client seat through Paddle — not a personal membership. ';

/**
 * Cancel refused on a PAUSED coaching subscription (#4418).
 *
 * A paused subscription has no next billing period, so the only cancel Paddle
 * allows is an immediate one — and that ends the subscription permanently: it
 * can never be resumed, and every client needs a fresh grant after the coach
 * subscribes again. The clients already have no Premium through a paused seat,
 * so nothing is gained by that one irreversible press. The message says what the
 * tidy route costs (a resume bills a fresh period) and names the Paddle
 * dashboard as the way to end it without resuming, deliberately, rather than
 * offering a second "cancel now" button here.
 *
 * The personal Paddle rail still cancels a paused subscription immediately. The
 * difference is the blast radius — one dormant membership against a coach's
 * whole client roster — and is argued in design-docs/subscription-ops-tooling.md.
 */
export const COACH_SEAT_PAUSED_CANCEL_REFUSAL = COACH_SEAT_PREFIX
  + 'It is paused, so its clients already have no Premium through it. Cancelling a paused subscription ends it '
  + 'at once and for good: it could never be resumed. To end it tidily, resume it first and then cancel at period '
  + 'end — but resuming starts a new billing period, charged at the current seat count. To end it without '
  + 'resuming, cancel it in the Paddle dashboard.';

/** Cancel refused on a coach-seat record with no Paddle subscription id — a
 *  Waiting or Temporary seat. The same sentence `CancelTestCoachSeat` gives, so
 *  the two actions describe the same record the same way. */
const COACH_SEAT_NO_ID_CANCEL_REFUSAL =
  'This user\'s coach-seat record has no Paddle subscription id, so there is nothing to cancel at Paddle.';

/**
 * PERMANENT, and the reason is worth stating: a coaching
 * subscription has only ONE plan. What the coach pays is the seat count, and the
 * seat count follows their client list on its own. So there is no second plan to
 * move to — not now, and not after Phases 2 and 3.
 */
export const COACH_SEAT_SWITCH_PLAN_REFUSAL = COACH_SEAT_PREFIX
  + 'A coaching subscription has only one plan, so there is nothing to switch to. What the coach pays follows '
  + 'their client list by itself — each active client is a seat, and the count changes as clients join and leave.';

/** How the coach's own store is named and reached, for the `CoachTier`
 *  refusals. One place, so the Apple and Google wordings are not hand-copied
 *  across the four actions that refuse this rail. */
function describeCoachStore(provider: 'apple' | 'googlePlay' | undefined): { name: string; route: string } {
  if (provider === 'googlePlay') {
    return { name: 'Google Play', route: 'in the Google Play Store: Menu → Subscriptions' };
  }
  if (provider === 'apple') {
    return { name: 'Apple', route: 'on their iPhone: Settings → Apple Account → Subscriptions' };
  }
  // An older or half-written block may carry no provider. Say plainly that we do
  // not know which store rather than naming the wrong one — sending a coach to
  // Apple when they bought through Google is worse than admitting we cannot tell.
  return {
    name: 'an app store',
    route: 'in whichever store they bought it from (we have no record of which one that is)',
  };
}

/**
 * The `CoachTier` refusals. All PERMANENT: the coach bought this subscription
 * inside Apple or Google Play, and neither store gives a merchant an API to
 * cancel, restart or re-plan somebody else's purchase.
 *
 * Exported so the tests assert against this, not against a second copy of the
 * sentence — the same reason `APPLE_CANCEL_REFUSAL` and its siblings are
 * exported constants. It is a function rather than a constant only because the
 * store's name and route come from the block.
 */
export function coachTierRefusal(
  provider: 'apple' | 'googlePlay' | undefined,
  /** What we cannot do on the coach's behalf, e.g. `'cancel a coaching subscription'`. */
  whatWeCannotDo: string,
  /** What the COACH must do instead, ending before the store route. */
  whatTheCoachMustDo: string,
): string {
  const store = describeCoachStore(provider);
  return `This is a coaching subscription bought through ${store.name}, and we cannot ${whatWeCannotDo} `
    + `on the coach's behalf. ${whatTheCoachMustDo} ${store.route}.`;
}

/** What each remaining licence type is told when the PayPro fallback finds
 *  nothing. Split out so every arm's wording sits together and can be read
 *  against the others — they are the same sentence for four quite different
 *  situations otherwise. */
function refuseSynchronizeByLicenseType(
  licenseType: UserLicenseType | undefined,
): string {
  // Note which licence types are NOT here any more: since #4416 both coach
  // rails have real Re-sync support and real refusals of their own in
  // `resolveLicenseRailSynchronizeCapability`, so neither reaches this function. Phase 1's
  // two interim wordings went with them.
  switch (licenseType) {
    case UserLicenseType.Coach:
      // a CLIENT, not a coach: their Premium is granted by whoever coaches them,
      // so there is no provider record of their own to re-read. Point at the
      // account that DOES have one, rather than leaving support here.
      return 'This user\'s Premium comes from their coach\'s subscription, not from a payment provider of their own. '
        + 'Re-syncing the COACH\'s subscription is what would help here.';
    case UserLicenseType.Key:
      return 'This user\'s access comes from a redeemed activation key, which has no payment provider to re-read from.';
    default:
      // genuinely nothing to re-read — say that plainly rather than blaming
      // PayPro for a user who never had a PayPro subscription.
      return 'This user has no subscription we can re-sync from a payment provider.';
  }
}

/**
 * True when a Re-sync must ALSO re-read the coach's Paddle coaching seat, beside
 * the subscription the licence type names (#5958).
 *
 * The licence type names ONE subscription, the one that currently wins the
 * licence. A seat whose webhook was lost reads not-active, and a not-active
 * block always loses to a personal sub that still grants access, so without
 * this the seat is never re-read in exactly the case Re-sync exists for. When
 * the licence type already IS the seat, it is read once, as the licence rail.
 */
export function synchronizeAlsoReadsCoachSeat(
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): boolean {
  return licenseType !== UserLicenseType.CoachSeat && !!userInfo.coachSeatData?.subscriptionId;
}

/**
 * The re-sync decision: the subscription the licence type names, plus the
 * coach seat when `synchronizeAlsoReadsCoachSeat` says so (#5958). A licence
 * subscription that cannot be read does not refuse the action when the seat
 * can be; the server then reads the seat alone.
 */
function resolveSynchronizeCapability(
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): ActionCapability {
  const licenseRail = resolveLicenseRailSynchronizeCapability(licenseType, userInfo);
  if (!licenseRail.supported && synchronizeAlsoReadsCoachSeat(licenseType, userInfo)) {
    return { supported: true };
  }
  return licenseRail;
}

/**
 * Whether the subscription the user's licence type names can be re-synced,
 * dispatched on the user's license type (#4248 Phase 3). The server asks this
 * directly to decide whether to read that subscription at all, when the coach
 * seat is also being read (#5958).
 *
 * Every provider is supported here — this is the fix for the #4227 class, where
 * a change made in a provider's own dashboard never reaches us and the customer
 * is left looking at a stale membership. What each branch checks is simply
 * whether we hold the provider-side identifier that provider's reconciler reads
 * from; without it there is nothing to ask the provider ABOUT.
 */
export function resolveLicenseRailSynchronizeCapability(
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): ActionCapability {
  switch (licenseType) {
    case UserLicenseType.Apple:
      return userInfo.appleData?.originalTransactionId
        ? { supported: true }
        : { supported: false, reason: 'This Apple account has no original transaction id to sync from.' };
    case UserLicenseType.GooglePlay:
      // the Google Play reconciler validates by (purchaseToken, productId) — it
      // needs BOTH, and quietly returns "nothing changed" if either is missing.
      return userInfo.googlePlayData?.purchaseToken && userInfo.googlePlayData?.productId
        ? { supported: true }
        : { supported: false, reason: 'This Google Play account is missing the purchase token or product id needed to sync from the Play Store.' };
    case UserLicenseType.Paddle:
      return userInfo.paddleData?.subscriptionId
        ? { supported: true }
        : { supported: false, reason: 'This user has no Paddle subscription id to sync from.' };
    case UserLicenseType.PayPro:
      return userInfo.payProData?.subscriptionId
        ? { supported: true }
        : refusePayProOnlyAction('Synchronize', licenseType);
    case UserLicenseType.CoachSeat:
      // The Paddle per-seat COACHING subscription — a different subscription
      // from `paddleData`, which a coach may also hold. A coach is now re-synced
      // against their coaching subscription, never against a leftover PayPro
      // record (#4416; the reasoning for dropping that fallback is in
      // design-docs/subscription-ops-tooling.md).
      return userInfo.coachSeatData?.subscriptionId
        ? { supported: true }
        : { supported: false, reason: 'This coach has no coaching-subscription id on file to re-sync from Paddle.' };
    case UserLicenseType.CoachTier:
      // The app-store coaching rail. Each store needs its own identifiers, and
      // asking for the other store's would report every record from one store as
      // incomplete — which is why the handler carries two rails here rather than
      // one.
      if (userInfo.coachTierData?.provider === 'apple') {
        return userInfo.coachTierData?.originalTransactionId
          ? { supported: true }
          : { supported: false, reason: 'This coaching subscription has no Apple original transaction id to re-sync from.' };
      }
      if (userInfo.coachTierData?.provider === 'googlePlay') {
        // the Google Play reader validates by (purchaseToken, productId) — it
        // needs BOTH, exactly as the personal Google Play arm above does.
        return userInfo.coachTierData?.purchaseToken && userInfo.coachTierData?.productId
          ? { supported: true }
          : { supported: false, reason: 'This coaching subscription is missing the purchase token or product id needed to re-sync from the Play Store.' };
      }
      // No block, or a block that cannot say which store it came from. Refuse by
      // name rather than guessing a store: an older or half-written block is
      // exactly the case where naming the wrong one sends support to a screen
      // that cannot help them.
      return {
        supported: false,
        reason: 'This coaching subscription does not record which app store it was bought from, so there is nothing to re-sync it against.',
      };
    default:
      // What still reaches this arm: a coached CLIENT, a redeemed activation
      // key, and anything else with no rail of its own. Each can still be
      // sitting on top of a real PayPro subscription, and that user WAS
      // re-syncable before the action was widened — so the old fallback stays
      // for them: their PayPro record is exactly the thing support may need to
      // re-read.
      //
      // The two COACH licence types no longer arrive here. #4416 gave them real
      // cases above, which means a coach's Re-sync now reads their coaching
      // subscription and no longer falls back to a leftover PayPro record. What
      // that costs, and what support keeps, is argued in
      // design-docs/subscription-ops-tooling.md — the short version is that the
      // fallback only ever refreshed one expiry date on a retired block, while
      // every way of READING that block, and Terminate / ChangeProduct / Cleanup
      // acting on it, are untouched.
      if (userInfo.payProData?.subscriptionId) {
        return { supported: true };
      }
      // Nothing to re-read through the PayPro fallback, so say what this user
      // actually has. Until #4362 every licence type here got one sentence
      // claiming there was no subscription at all — false for a coach, whose
      // coaching subscription is printed a few lines above on the same screen.
      return { supported: false, reason: refuseSynchronizeByLicenseType(licenseType) };
  }
}

/**
 * The messages shown when an admin tries to resume a store subscription. Once an
 * App Store / Play Store subscription has been cancelled, only the customer can
 * start it again — there is no merchant-side "undo" for us to call.
 */
export const APPLE_RESUME_REFUSAL =
  'We cannot restart an Apple subscription on the customer\'s behalf. '
  + 'The customer must re-subscribe on their iPhone: Settings → Apple Account → Subscriptions '
  + '(App Store subscriptions are managed by Apple, not by us).';

export const GOOGLE_PLAY_RESUME_REFUSAL =
  'We cannot restart a Google Play subscription on the customer\'s behalf. '
  + 'The customer must re-subscribe in the Google Play Store: Menu → Subscriptions '
  + '(Google Play subscriptions are managed by Google, not by us).';

/**
 * The resume sub-decision (#4248 Phase 4), dispatched on the user's license type.
 *
 * Resume is only offered where there is genuinely something to undo. Showing it
 * on a healthy subscription would be a button that either does nothing or, worse,
 * calls a provider endpoint that fails — so each rail states what "something to
 * undo" actually means for it.
 */
function resolveResumeCapability(
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): ActionCapability {
  // the reconciled top-level status. Both rails below consult it as well as
  // their own block, because the two can legitimately disagree — see each case.
  const topLevelStatus = userInfo.subscriptionStatus;
  switch (licenseType) {
    case UserLicenseType.Paddle: {
      const paddle = userInfo.paddleData;
      if (!paddle?.subscriptionId) {
        return { supported: false, reason: 'This user has no Paddle subscription to resume.' };
      }
      // A DEAD subscription is not resumable, and must be checked first. A stale
      // `subscription.updated` can leave a pending change sitting on a Terminated
      // block, and without this guard we would offer support a Resume that Paddle
      // simply refuses. The customer-facing `computeCanResumeSubscription` guards
      // exactly the same way — keep the two in step.
      const isDead = paddle.subscriptionStatus === SubscriptionStatus.Terminated
        || topLevelStatus === SubscriptionStatus.Terminated;
      if (isDead) {
        return {
          supported: false,
          reason: 'This Paddle subscription has ended and cannot be resumed. The customer needs to subscribe again.',
        };
      }
      // Two undoable states. First: a change Paddle has SCHEDULED but not yet
      // applied — and only a CANCEL or a PAUSE, never a scheduled `resume`.
      // Paddle can schedule a resume too, and "undoing" that would delete it,
      // which is the opposite of what the operator pressed. Second: a pause that
      // has already taken effect, which Paddle's resume endpoint undoes.
      const pendingAction = paddle.scheduledChange?.action;
      const hasUndoablePendingChange = pendingAction === 'cancel' || pendingAction === 'pause';
      const hasPaused = paddle.subscriptionStatus === SubscriptionStatus.Paused;
      if (!hasUndoablePendingChange && !hasPaused) {
        // deliberately says "nothing to resume" rather than "running normally":
        // a subscription whose payment is failing (Paddle `past_due`, stored as
        // Suspended) is NOT running normally, but Resume cannot fix a failed
        // charge either, so the button is still correctly withheld.
        return {
          supported: false,
          reason: 'This Paddle subscription has no pending cancellation or pause — there is nothing to resume.',
        };
      }
      return { supported: true };
    }
    case UserLicenseType.PayPro: {
      const payPro = userInfo.payProData;
      if (!payPro?.subscriptionId) {
        return { supported: false, reason: 'This user has no PayPro subscription to resume.' };
      }
      // PayPro's cancel-at-period-end leaves the subscription Suspended, which is
      // exactly the state its Renew call undoes. Anything else is not resumable.
      //
      // Accept EITHER the top-level status or the block mirror. The rest of the
      // system (payProResubscribe, computeCanResumeSubscription) reads the
      // top-level field, while the block copy is only filled in lazily by
      // `migrateBlock` when a reconcile happens to run and be saved — so a
      // legacy or not-yet-reconciled document can be genuinely suspended with an
      // empty block field. Reading only one of the two would refuse a real
      // suspended subscriber with a confusing "is 'none', not suspended".
      const isSuspended = payPro.subscriptionStatus === SubscriptionStatus.Suspended
        || topLevelStatus === SubscriptionStatus.Suspended;
      if (!isSuspended) {
        return {
          supported: false,
          reason: `This PayPro subscription is '${payPro.subscriptionStatus ?? topLevelStatus ?? 'none'}', not suspended — there is nothing to resume.`,
        };
      }
      return { supported: true };
    }
    case UserLicenseType.Apple:
      return { supported: false, reason: APPLE_RESUME_REFUSAL };
    case UserLicenseType.GooglePlay:
      return { supported: false, reason: GOOGLE_PLAY_RESUME_REFUSAL };
    case UserLicenseType.CoachSeat: {
      // #4418. The same three undoable states the Paddle arm above accepts, read
      // off the seat block. The already-Paused case is load-bearing: a paused
      // seat is refused Cancel with "resume it first", so Resume must reach it.
      // "Ended" reads both the block and the top-level status, exactly as the
      // Paddle arm does; in this arm `licenseType` is CoachSeat, so the
      // top-level status is this seat's own.
      const seat = userInfo.coachSeatData;
      if (!seat?.subscriptionId) {
        return {
          supported: false,
          reason: 'This coach has no coaching-subscription id on file, so there is nothing to resume at Paddle.',
        };
      }
      if (seat.subscriptionStatus === SubscriptionStatus.Terminated || topLevelStatus === SubscriptionStatus.Terminated) {
        return {
          supported: false,
          reason: 'This coaching subscription has ended and cannot be resumed. The coach needs to subscribe again.',
        };
      }
      const pendingAction = seat.scheduledChange?.action;
      const hasUndoablePendingChange = pendingAction === 'cancel' || pendingAction === 'pause';
      if (!hasUndoablePendingChange && seat.subscriptionStatus !== SubscriptionStatus.Paused) {
        return {
          supported: false,
          reason: 'This coaching subscription has no pending cancellation or pause — there is nothing to resume.',
        };
      }
      return { supported: true };
    }
    case UserLicenseType.CoachTier:
      return {
        supported: false,
        reason: coachTierRefusal(userInfo.coachTierData?.provider, 'restart a coaching subscription',
          'The coach must subscribe again themselves'),
      };
    default:
      return { supported: false, reason: 'This user has no subscription that we can resume.' };
  }
}

/**
 * The messages shown when an admin tries to switch a store subscription's plan.
 * Apple and Google both let the customer change plan themselves inside their own
 * store, and neither gives a merchant an API to do it on the customer's behalf.
 */
export const APPLE_SWITCH_PLAN_REFUSAL =
  'We cannot change an Apple subscription\'s plan on the customer\'s behalf. '
  + 'The customer can switch between monthly and yearly on their iPhone: '
  + 'Settings → Apple Account → Subscriptions (App Store subscriptions are managed by Apple, not by us).';

export const GOOGLE_PLAY_SWITCH_PLAN_REFUSAL =
  'We cannot change a Google Play subscription\'s plan on the customer\'s behalf. '
  + 'The customer can switch between monthly and yearly in the Google Play Store: '
  + 'Menu → Subscriptions (Google Play subscriptions are managed by Google, not by us).';

/**
 * The switch-plan sub-decision (#4248 Phase 7), dispatched on the user's license
 * type. Paddle-only, and refused for everything else BY NAME.
 *
 * A plan switch bills `prorated_immediately` — a real charge — so this refuses
 * every state where that charge would be wrong. It deliberately does NOT check
 * the price pair: resolving the target needs server configuration, which this
 * pure shared module has no access to, so the handler does that last check. What
 * lives here is everything both front-ends need in order to decide whether to
 * even OFFER the action.
 */
function resolveSwitchPlanCapability(
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): ActionCapability {
  switch (licenseType) {
    case UserLicenseType.Paddle: {
      const paddle = userInfo.paddleData;
      if (!paddle?.subscriptionId) {
        return { supported: false, reason: 'This user has no Paddle subscription whose plan we can change.' };
      }
      // a dead subscription has no plan to move. Checked first, like Resume does,
      // because the states below can legitimately linger on a Terminated block.
      const isDead = paddle.subscriptionStatus === SubscriptionStatus.Terminated
        || userInfo.subscriptionStatus === SubscriptionStatus.Terminated;
      if (isDead) {
        return {
          supported: false,
          reason: 'This Paddle subscription has ended, so there is no plan to change. The customer needs to subscribe again.',
        };
      }
      if (paddle.subscriptionStatus === SubscriptionStatus.Paused) {
        return {
          supported: false,
          reason: 'This Paddle subscription is paused. Switching plans charges the customer straight away, '
            + 'which is the opposite of what a paused subscription is for — resume it first.',
        };
      }
      if (paddle.scheduledChange) {
        return {
          supported: false,
          reason: `This Paddle subscription is already scheduled to ${paddle.scheduledChange.action} at the end of the period. `
            + 'Switching plans would charge the customer for a subscription that is about to end — resume it first if they are staying.',
        };
      }
      // Note what is deliberately NOT refused: a `Suspended` (payment-failing)
      // Paddle subscription. The customer-facing page hides its own Switch
      // button in that state, so the admin rail is knowingly more permissive —
      // support may reasonably want to move a struggling customer onto the
      // cheaper monthly plan, and a switch Paddle rejects bills nothing. If
      // Paddle turns out to refuse these routinely, refuse it here instead: the
      // operator would otherwise get the deliberately-uncertain `switch_failed`
      // message for a precondition we could have known up front.
      //
      // Paddle rejects a plan change on a trialing subscription, so without this
      // the button would render and the CLI dry run would promise a switch that
      // the real call always refuses. Asked through the shared clock-free Paddle
      // rule rather than a hand-written `paidStartCounted` test — a second copy
      // of that rule is exactly how #4184 happened.
      if (isPaddleSubscriberInFreeTrial(paddle)) {
        return {
          supported: false,
          reason: 'This customer is still in their free trial, and Paddle does not allow a plan change during a trial. '
            + 'They can switch once the trial has converted to a paid subscription.',
        };
      }
      // A legacy-price grant (#5084) is a discount restricted to the price the
      // customer bills on now, so a switch costs them the granted amount and
      // they start paying full price. Mirrors the same refusal in
      // `switchPaddlePlanForUserAsync` — the server enforces it, this is what
      // stops the button and the CLI dry run promising a switch it will refuse.
      if (paddle.discountId) {
        return {
          supported: false,
          reason: 'This customer has been granted their old PayPro price, and that grant only applies to the plan '
            + 'they are on now — switching would silently put them back on the full price. Switch the plan only if '
            + 'they have asked for it, and grant the old price again afterwards so it lands on the new plan.',
        };
      }
      return { supported: true };
    }
    case UserLicenseType.PayPro:
      // PayPro plan changes have always been ChangeProduct. Name it, rather than
      // saying "not supported" and sending support hunting for a missing feature
      // that is in fact right there.
      return {
        supported: false,
        reason: 'Use the PayPro \'ChangeProduct\' action to move a PayPro subscription to a different plan.',
      };
    case UserLicenseType.Apple:
      return { supported: false, reason: APPLE_SWITCH_PLAN_REFUSAL };
    case UserLicenseType.GooglePlay:
      return { supported: false, reason: GOOGLE_PLAY_SWITCH_PLAN_REFUSAL };
    case UserLicenseType.CoachSeat:
      return { supported: false, reason: COACH_SEAT_SWITCH_PLAN_REFUSAL };
    case UserLicenseType.CoachTier:
      return {
        supported: false,
        reason: coachTierRefusal(userInfo.coachTierData?.provider, 'change a coaching subscription\'s tier',
          'Moving to a different tier means the coach buying that tier themselves'),
      };
    default:
      return { supported: false, reason: 'This user has no subscription whose plan we can change.' };
  }
}

/**
 * Refuses a cancel on a Paddle block that has already ended, for the personal
 * Paddle rail and the coach seat alike (#4418), so the two keep one copy of the
 * rule. It reads the BLOCK's own status — the subscription Paddle would be asked
 * to cancel — and deliberately not the reconciled top-level status, which can
 * say Terminated while this block is still live. Kept out of
 * `requireWebRailSubId`, which PayPro shares: PayPro's cancel rules are not
 * changing.
 */
function refuseEndedPaddleBlock(
  block: { subscriptionStatus?: SubscriptionStatus } | undefined,
  whatEnded: string,
): ActionCapability | undefined {
  return block?.subscriptionStatus === SubscriptionStatus.Terminated
    ? { supported: false, reason: `${whatEnded} has already ended, so there is nothing to cancel.` }
    : undefined;
}

/** The cancel-at-period-end sub-decision, dispatched on the user's license type. */
function resolveCancelCapability(
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): ActionCapability {
  switch (licenseType) {
    case UserLicenseType.Paddle:
      return refuseEndedPaddleBlock(userInfo.paddleData, 'This Paddle subscription')
        ?? requireWebRailSubId(userInfo.paddleData?.subscriptionId, 'Paddle');
    case UserLicenseType.PayPro:
      return requireWebRailSubId(userInfo.payProData?.subscriptionId, 'PayPro');
    case UserLicenseType.Apple:
      return { supported: false, reason: APPLE_CANCEL_REFUSAL };
    case UserLicenseType.GooglePlay:
      return { supported: false, reason: GOOGLE_PLAY_CANCEL_REFUSAL };
    case UserLicenseType.CoachSeat: {
      // #4418. The seat is a DIFFERENT subscription from `paddleData`; every rule
      // here reads `coachSeatData`. The client-count confirmation is the front
      // ends' job, not this pure matrix's.
      const seat = userInfo.coachSeatData;
      if (!seat?.subscriptionId) {
        return { supported: false, reason: COACH_SEAT_NO_ID_CANCEL_REFUSAL };
      }
      const ended = refuseEndedPaddleBlock(seat, 'This coaching subscription');
      if (ended) return ended;
      if (seat.subscriptionStatus === SubscriptionStatus.Paused) {
        return { supported: false, reason: COACH_SEAT_PAUSED_CANCEL_REFUSAL };
      }
      return { supported: true };
    }
    case UserLicenseType.CoachTier:
      return {
        supported: false,
        reason: coachTierRefusal(userInfo.coachTierData?.provider, 'cancel a coaching subscription',
          'The coach must cancel it themselves'),
      };
    default:
      return { supported: false, reason: 'This user has no active subscription that we can cancel.' };
  }
}

/**
 * The `CancelTestCoachSeat` sub-decision (#4755).
 *
 * Deliberately NOT dispatched on `licenseType`, unlike every other rule in this
 * file, and that difference is the point rather than an oversight. `licenseType`
 * describes which provider wins for the user's PERSONAL membership; a coach's
 * seat subscription is a separate Paddle subscription that can sit beside a
 * personal one on any rail, or beside none at all. Asking `licenseType` here
 * would refuse coaches whose personal membership happens to be on Apple while
 * their coaching seat sits perfectly cancellable in Paddle.
 *
 * Every arm is a refusal except the one that is provably a sandbox test purchase.
 * The `isTestPurchase` check is the load-bearing one: without it this action would
 * cancel a real coach's seat, which ends that coach's clients' Premium through the
 * coach licence mirror. The server re-checks it too — this copy exists so the CLI
 * can refuse before a round trip, not so the server can trust it.
 */
function resolveCancelTestCoachSeatCapability(userInfo: ActionCapabilityUserView): ActionCapability {
  const seat = userInfo.coachSeatData;
  if (!seat) {
    return { supported: false, reason: 'This user has no Paddle coach-seat subscription.' };
  }
  if (!seat.subscriptionId) {
    return { supported: false, reason: COACH_SEAT_NO_ID_CANCEL_REFUSAL };
  }
  if (seat.isTestPurchase !== true) {
    return {
      supported: false,
      reason: 'This coach seat is a REAL purchase, not a sandbox test one, and this action only ever cancels test purchases. '
        + 'Cancelling a real coach\'s seat also ends their clients\' Premium, so it is not available here.',
    };
  }
  return { supported: true };
}

/**
 * The "Grant Free Days" sub-decision (#3949, narrowed by #4362).
 *
 * It used to answer `{ supported: true }` for EVERY licence type, on the
 * grounds that the action is provider-agnostic. It is provider-agnostic across
 * the four PAYMENT providers, and not beyond them: `changeNextPaymentDate` in
 * `server/functions/src/functions/adminChangeSubscription.ts` dispatches on
 * `licenseType` and refuses everything else. So a coach reached the server,
 * failed there, and left behind a malformed audit row filed under `payPro` with
 * `subscriptionId: 0`.
 *
 * This MIRRORS that handler's own `switch`. If a branch is ever added there,
 * one must be added here too — a silent disagreement between the two is exactly
 * what produced the mis-filed row. Note the consequence of refusing here rather
 * than at the server: a capability refusal returns BEFORE the audit block, so a
 * coach grant attempt now writes NO row at all instead of a wrong one. That is
 * deliberate (see `design-docs/subscription-ops-tooling.md`).
 */
function resolveGrantFreeDaysCapability(licenseType: UserLicenseType | undefined): ActionCapability {
  switch (licenseType) {
    case UserLicenseType.PayPro:
    case UserLicenseType.GooglePlay:
    case UserLicenseType.Apple:
    case UserLicenseType.Paddle:
      // each provider branch in the handler validates its own prerequisites —
      // Apple's twice-a-year limit, Paddle's need for a next billing date, and
      // so on. Those are not knowable from this pure module.
      return { supported: true };
    case UserLicenseType.CoachSeat:
    case UserLicenseType.CoachTier:
      return {
        supported: false,
        reason: 'Grant Free Days works on a personal membership, by pushing out the customer\'s next charge at '
          + 'their payment provider. This is a coaching subscription, and we have no way to push out a coaching '
          + 'subscription\'s next charge. If a make-good is genuinely needed, arrange it with the coach directly.',
      };
    case UserLicenseType.Coach:
      return {
        supported: false,
        reason: 'This user\'s Premium is granted by their coach, so they have no charge of their own to push out. '
          + 'Extending the COACH\'s subscription is what would help here.',
      };
    case UserLicenseType.Key:
      return {
        supported: false,
        reason: 'This user\'s access comes from a redeemed activation key, which has no next charge to push out. '
          + 'Issue them a longer key instead.',
      };
    default:
      return {
        supported: false,
        reason: 'This user has no subscription with a next charge that we could push out.',
      };
  }
}

/** Refusal shown when a legacy-price grant is asked for on a provider that
 *  cannot have one. The grant attaches a discount to a PADDLE subscription; the
 *  problem it solves — being moved off PayPro onto a higher Paddle price — only
 *  exists on Paddle, so there is nothing to say to the other rails except which
 *  provider this is about. */
function refuseGrantLegacyPriceProvider(licenseType: UserLicenseType | undefined): ActionCapability {
  const named = licenseType === undefined ? 'no subscription with us' : `a ${licenseType} subscription`;
  return {
    supported: false,
    reason: 'Granting the old PayPro price only applies to a Paddle subscription — it works by discounting the '
      + `Paddle price back to what PayPro charged. This customer has ${named}.`,
  };
}

/**
 * The legacy-price grant sub-decision (#5084). Paddle-only, and refused for
 * everything else by name.
 *
 * What it deliberately does NOT decide: whether the customer *deserves* the
 * grant. The policy is "on request, to somebody we moved from PayPro to Paddle
 * who did not choose to leave in between", and what "did not stop in between"
 * means for a lapse caused by PayPro failing to charge a card has never been
 * settled (owner decision 2026-09-08: leave it to the officer). Encoding a rule
 * nobody has written would refuse the exact customer the policy exists for. The
 * audit row records which officer granted it.
 *
 * It also does not check the amounts. Those need the customer's live Paddle
 * quote, which this pure shared module cannot read — the handler does that, and
 * refuses there when the currency is not covered or the customer is already at
 * or below the old price.
 */
function resolveGrantLegacyPriceCapability(
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): ActionCapability {
  if (licenseType !== UserLicenseType.Paddle) return refuseGrantLegacyPriceProvider(licenseType);
  const paddle = userInfo.paddleData;
  if (!paddle?.subscriptionId) {
    return { supported: false, reason: 'This user has no Paddle subscription to grant the old price on.' };
  }
  // a dead subscription has no future charge to discount. Checked before the
  // states below, exactly as the switch-plan and resume rules do, because those
  // can legitimately linger on a Terminated block.
  const isDead = paddle.subscriptionStatus === SubscriptionStatus.Terminated
    || userInfo.subscriptionStatus === SubscriptionStatus.Terminated;
  if (isDead) {
    return {
      supported: false,
      reason: 'This Paddle subscription has ended, so there is no future charge to apply the old price to. '
        + 'The customer needs to subscribe again first.',
    };
  }
  if (paddle.discountId) {
    return {
      supported: false,
      reason: 'This customer already has a discount attached to their Paddle subscription, so the old price '
        + 'has already been granted. Check it in Paddle before granting anything else — a second grant would stack.',
    };
  }
  // Note what is NOT refused: a subscription still in its free trial. That is
  // the common case rather than an edge one — every customer we moved off
  // PayPro arrives on a fresh trial, which is exactly when they notice the
  // higher price and ask. Paddle applies the discount from the next billing
  // period, which IS the first real charge, so the grant lands where it should.
  // A paused or payment-failing subscription is not refused either: the grant
  // changes only what the next charge comes to, and bills nothing itself.
  return { supported: true };
}

/**
 * Decide whether an admin subscription action can run for this user, or must be
 * refused with a plain-language reason.
 *
 * It began (#4248 Phase 2) as a pure relocation of the old PayPro-only guard,
 * preserving every existing action's behaviour exactly. That is no longer true
 * and the note is kept rather than deleted so nobody re-derives it: #4362
 * NARROWED `ChangeNextPaymentDate`, which used to answer yes for every licence
 * type and now mirrors what its server handler can actually serve.
 *
 * `action` is intentionally a bare `string`, not the `AdminChangeSubscriptionParams`
 * action union: it is the value the admin callable receives over the wire, so an
 * unrecognized action must be REFUSED at runtime (the `default` branch), not turned
 * into a compile error. The CLI passes it through the same way.
 */
export function resolveActionCapability(
  action: string,
  licenseType: UserLicenseType | undefined,
  userInfo: ActionCapabilityUserView,
): ActionCapability {
  switch (action) {
    case 'Cleanup':
      // clears local PayPro fields only; always permitted, exactly as the old
      // guard exempted it.
      return { supported: true };

    case 'ChangeNextPaymentDate':
      return resolveGrantFreeDaysCapability(licenseType);

    case 'ChangeProduct':
    case 'Terminate':
      // PayPro-only, and both need a live PayPro subscription id — the same
      // requirement the old guard enforced.
      return userInfo.payProData?.subscriptionId
        ? { supported: true }
        : refusePayProOnlyAction(action, licenseType);

    case 'Synchronize':
      return resolveSynchronizeCapability(licenseType, userInfo);

    case 'CancelAtPeriodEnd':
      return resolveCancelCapability(licenseType, userInfo);

    case 'Resume':
      return resolveResumeCapability(licenseType, userInfo);

    case 'SwitchPlan':
      return resolveSwitchPlanCapability(licenseType, userInfo);

    case 'GrantLegacyPrice':
      return resolveGrantLegacyPriceCapability(licenseType, userInfo);

    case 'CancelTestCoachSeat':
      // reads coachSeatData, not the licenseType-selected personal block — see
      // the helper for why this one does not dispatch on licenseType.
      return resolveCancelTestCoachSeatCapability(userInfo);

    case 'RepointSubscription':
      // The real rules live in `planSubscriptionRepoint`, because the decision
      // needs BOTH accounts and this matrix only ever sees one. So this stays
      // deliberately PERMISSIVE and lets the plan speak: refusing here on a
      // narrower test would replace the plan's accurate reason (for example
      // "bought through Apple — the customer must cancel there") with a vaguer
      // one, sending the operator hunting for the wrong problem.
      return { supported: true };

    case 'RebindAppleAccount':
      // Permissive for the same reason as the repoint: the account this runs on
      // has, by construction, never held the subscription, so nothing on it can
      // say no. The handler asks Apple and the ownership check, which can — and
      // it grants through the same sequence an ordinary Apple purchase does, so
      // whatever that sequence refuses is refused here too (#5384).
      return { supported: true };

    default:
      return { supported: false, reason: `Unknown action '${action}'.` };
  }
}
